'use no memo';

import {
  type ColumnDef,
  type GroupingState,
  type ExpandedState,
  type Row,
  flexRender,
  getCoreRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState, type ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  initialGrouping?: GroupingState;
  renderGroupHeader?: (row: Row<TData>) => ReactNode;
  emptyMessage?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  initialGrouping,
  renderGroupHeader,
  emptyMessage = 'No files uploaded.',
}: DataTableProps<TData, TValue>) {
  const [grouping, setGrouping] = useState<GroupingState>(
    initialGrouping ?? []
  );
  const [expanded, setExpanded] = useState<ExpandedState>(true);

  // TanStack Table returns non-memoisable functions — opt out of compiler optimisation.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: { grouping, expanded },
    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
  });

  return (
    <div className="overflow-hidden rounded-md mt-3">
      <Table className="table-fixed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={
                    (header.column.columnDef.meta as { className?: string })
                      ?.className
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              if (row.getIsGrouped() && renderGroupHeader) {
                return (
                  <TableRow
                    key={row.id}
                    className="bg-muted/50 hover:bg-muted/50 cursor-pointer"
                    onClick={row.getToggleExpandedHandler()}
                  >
                    <TableCell colSpan={columns.length}>
                      {renderGroupHeader(row)}
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {cell.getIsAggregated() || cell.getIsPlaceholder()
                        ? null
                        : flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
